import { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate } from "@tanstack/react-router"

import { cn } from "@productlane/lib/src/cn"
import { mutators } from "@productlane/zero/mutators"

import type { InboxState } from "@/lib/states/global-state"
import type { VirtualItem } from "@tanstack/react-virtual"
import { useShortcut } from "@/lib"
import { useThreadState } from "@/lib/states/global-state"
import { useZero } from "@/lib/zero"
import { moveTargetIndex } from "./helpers/list-neighbors"
import { readToggle } from "./helpers/read-toggle"

const noteStates: InboxState[] = ["new", "my", "open", "snoozed", "done"]

// Delay between the last arrow keypress and actually navigating to the
// selected thread. Long enough that holding the key skips all the
// intermediate navigations; short enough that a single keypress still
// feels instant.
const NAV_DEBOUNCE_MS = 70

// How long after the last arrow keypress the thread panel waits before it
// commits to loading. Key auto-repeat is ~30ms, so a held key still coalesces
// into one load. Kept separate from MOUSE_IGNORE_MS, which needs to be far
// longer and used to gate this too.
const NAV_SETTLE_MS = 120

// How long mouse-move is ignored after a keypress, so rows scrolling under a
// stationary cursor can't steal the selection.
const MOUSE_IGNORE_MS = 300

export function getListItemProps({
	threadIds,
	isKeyboardNav,
	selectedIndex,
	setSelectedIndex,
}: {
	threadIds: string[]
	isKeyboardNav: boolean
	selectedIndex: number | null
	setSelectedIndex: (index: number) => void
}) {
	return ({
		virtualItem,
		item,
		isHeadline,
	}: {
		virtualItem: VirtualItem
		item: { id: string }
		isHeadline: boolean
	}) => {
		const isSelected =
			virtualItem.index === selectedIndex &&
			!isHeadline &&
			!threadIds.includes(item.id)
		const isMultiSelected = !isHeadline && threadIds.includes(item.id)

		return {
			className: cn("rounded-md", {
				"bg-gray-100 dark:bg-gray-800": isSelected,
				"bg-gray-150 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-800/50":
					isMultiSelected,
				"ring-1 ring-inset ring-gray-300 dark:ring-gray-600":
					isMultiSelected && virtualItem.index === selectedIndex,
				"hover:bg-gray-50 dark:hover:bg-gray-800/50":
					!isSelected && !isMultiSelected && !isHeadline,
			}),
			isSelected,
			onMouseMove: () => {
				if (!isKeyboardNav) {
					setSelectedIndex(virtualItem.index)
				}
			},
			onMouseDown: () => {
				if (!isKeyboardNav) {
					setSelectedIndex(virtualItem.index)
				}
			},
		}
	}
}

function useListTabShortcuts({
	threadTab,
	setThreadTab,
	onTabSwitch,
	onDeselect,
	navShortcutsEnabled = true,
}: {
	threadTab: InboxState
	setThreadTab: (value: InboxState) => void
	onTabSwitch?: (direction: "forward" | "backward") => void
	onDeselect?: () => void
	/** Off when the combined sidebar owns Tab, so each key fires once. */
	navShortcutsEnabled?: boolean
}) {
	const { setInboxState, setActiveCustomViewId } = useThreadState()
	const navigate = useNavigate()

	const switchToTab = useCallback(
		(state: InboxState) => {
			setThreadTab(state)
			setInboxState(state)
			setActiveCustomViewId(null)
			void navigate({ to: "/threads", replace: true })
		},
		[setThreadTab, setInboxState, navigate, setActiveCustomViewId],
	)

	const changeTab = useCallback(
		(direction: "forward" | "backward") => {
			const currentIndex = noteStates.indexOf(threadTab)

			if (currentIndex === -1) {
				switchToTab("open")
				return
			}

			const newIndex =
				direction === "forward"
					? (currentIndex + 1) % noteStates.length
					: (currentIndex - 1 + noteStates.length) % noteStates.length

			const newState = noteStates[newIndex]
			if (newState) {
				switchToTab(newState)
			}
		},
		[threadTab, switchToTab],
	)

	useShortcut(
		"esc",
		() => {
			switchToTab("open")
			onDeselect?.()
		},
		{ preventDefault: true },
	)

	const handleTabSwitch = onTabSwitch ?? changeTab

	useShortcut(
		"tab",
		() => {
			handleTabSwitch("forward")
		},
		{ preventDefault: true, enabled: navShortcutsEnabled },
	)

	useShortcut("shift+tab", () => handleTabSwitch("backward"), {
		preventDefault: true,
		enabled: navShortcutsEnabled,
	})
}

interface NavThread {
	id: string
	isRead?: boolean
	state?: string | null
}

function useListVirtualizerShortcuts({
	getThreadAtIndex,
	threadsLength,
	scrollToIndex,
	threadIds,
	toggleThreadId,
	threadTab,
	onNavigate,
	resolveOpenThreadIndex,
	hasOpenThread = false,
}: {
	getThreadAtIndex: (index: number) => NavThread | undefined
	threadsLength: number
	scrollToIndex: (index: number) => void
	threadIds: string[]
	toggleThreadId: (id: string) => void
	threadTab: InboxState
	onNavigate?: (thread: NavThread) => void
	resolveOpenThreadIndex?: () => number | null
	hasOpenThread?: boolean
}) {
	const mouseIgnoreTimer = useRef<NodeJS.Timeout>(undefined)
	const navSettleTimer = useRef<NodeJS.Timeout>(undefined)
	const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
	const [isKeyboardNav, setIsKeyboardNav] = useState(false)
	const [isNavSettling, setIsNavSettling] = useState(false)
	const navigate = useNavigate()

	// Ref tracks latest selectedIndex. We update it SYNCHRONOUSLY when we
	// call setSelectedIndex, not on render — otherwise a held arrow key can
	// fire two keydown events before React commits, both reading the same
	// stale ref and computing the same "next" index → skipped rows. The
	// render-phase assignment below is a safety net for when external
	// effects (URL sync, etc.) update selectedIndex.
	const selectedIndexRef = useRef<number | null>(null)
	selectedIndexRef.current = selectedIndex

	// Keep refs to the row accessor + length so the navigation callbacks
	// don't have to re-bind every time the underlying paginated rows change
	// (which they do on every Zero sync).
	const getThreadAtIndexRef = useRef(getThreadAtIndex)
	getThreadAtIndexRef.current = getThreadAtIndex
	const threadsLengthRef = useRef(threadsLength)
	threadsLengthRef.current = threadsLength

	// The highlight is dropped whenever the open thread's row leaves the rendered
	// window, so j/k re-derive their position from the open thread instead.
	const resolveOpenThreadIndexRef = useRef(resolveOpenThreadIndex)
	resolveOpenThreadIndexRef.current = resolveOpenThreadIndex
	const hasOpenThreadRef = useRef(hasOpenThread)
	hasOpenThreadRef.current = hasOpenThread

	const currentIndex = useCallback(() => {
		const selected = selectedIndexRef.current
		if (selected !== null) return selected
		return resolveOpenThreadIndexRef.current?.() ?? null
	}, [])

	// Debounce navigate() — it triggers a heavy tree of work (URL change →
	// useParams → useInitThread → useQuery → ThreadContextProvider value →
	// top-bar/right-bar re-render). Firing it on every keypress of a held
	// arrow key stalls the render loop and makes the selection highlight
	// skip items. Instead: update the highlight + scroll instantly, and
	// only load the thread once the user stops pressing.
	// Keep a ref to onNavigate so scheduleNavigate doesn't have to re-bind
	// every render (parent passes a fresh closure each time).
	const onNavigateRef = useRef(onNavigate)
	onNavigateRef.current = onNavigate

	const navigateTimerRef = useRef<NodeJS.Timeout | null>(null)
	const scheduleNavigate = useCallback(
		(thread: NavThread) => {
			if (navigateTimerRef.current) {
				clearTimeout(navigateTimerRef.current)
			}
			navigateTimerRef.current = setTimeout(() => {
				void navigate({ to: "/threads/$id", params: { id: thread.id } })
				onNavigateRef.current?.(thread)
				navigateTimerRef.current = null
			}, NAV_DEBOUNCE_MS)
		},
		[navigate],
	)
	useEffect(() => {
		return () => {
			if (navigateTimerRef.current) {
				clearTimeout(navigateTimerRef.current)
			}
			clearTimeout(navSettleTimer.current)
			clearTimeout(mouseIgnoreTimer.current)
		}
	}, [])

	useEffect(() => {
		setSelectedIndex(null)
	}, [threadTab])

	const handleKeyboardNav = useCallback(
		(newIndex: number, direction: 1 | -1) => {
			setIsKeyboardNav(true)
			setIsNavSettling(true)
			setSelectedIndex(newIndex)
			// Keep the ref in sync immediately so a second keypress fired
			// before React commits still reads the correct "current" index.
			selectedIndexRef.current = newIndex

			// Scroll to one past the target so the row after the selection
			// stays visible — gives the Linear-style "peek at next item"
			// instead of the selected row hugging the edge of the viewport.
			const peekIndex = newIndex + direction
			const clampedPeek = Math.max(0, peekIndex)
			scrollToIndex(clampedPeek)

			clearTimeout(navSettleTimer.current)
			navSettleTimer.current = setTimeout(() => {
				setIsNavSettling(false)
			}, NAV_SETTLE_MS)

			clearTimeout(mouseIgnoreTimer.current)
			mouseIgnoreTimer.current = setTimeout(() => {
				setIsKeyboardNav(false)
			}, MOUSE_IGNORE_MS)
		},
		[scrollToIndex],
	)

	const moveDown = useCallback(() => {
		const length = threadsLengthRef.current
		const targetIndex = moveTargetIndex({
			index: currentIndex(),
			length,
			direction: "down",
			hasOpenThread: hasOpenThreadRef.current,
		})

		if (targetIndex !== null && targetIndex < length) {
			handleKeyboardNav(targetIndex, 1)
			const thread = getThreadAtIndexRef.current(targetIndex)
			if (thread) {
				scheduleNavigate(thread)
			}
		}
	}, [currentIndex, handleKeyboardNav, scheduleNavigate])

	const moveUp = useCallback(() => {
		const targetIndex = moveTargetIndex({
			index: currentIndex(),
			length: threadsLengthRef.current,
			direction: "up",
			hasOpenThread: hasOpenThreadRef.current,
		})

		if (targetIndex !== null && targetIndex >= 0) {
			handleKeyboardNav(targetIndex, -1)
			const thread = getThreadAtIndexRef.current(targetIndex)
			if (thread) {
				scheduleNavigate(thread)
			}
		}
	}, [currentIndex, handleKeyboardNav, scheduleNavigate])

	useShortcut(
		"meta+ArrowDown, ctrl+ArrowDown",
		() => {
			const length = threadsLengthRef.current
			if (length === 0) return
			const lastIndex = length - 1
			handleKeyboardNav(lastIndex, 1)
			const thread = getThreadAtIndexRef.current(lastIndex)
			if (thread) {
				scheduleNavigate(thread)
			}
		},
		{ preventDefault: true },
	)

	useShortcut(
		"meta+ArrowUp, ctrl+ArrowUp",
		() => {
			if (threadsLengthRef.current === 0) return
			handleKeyboardNav(0, -1)
			const thread = getThreadAtIndexRef.current(0)
			if (thread) {
				scheduleNavigate(thread)
			}
		},
		{ preventDefault: true },
	)

	useShortcut(
		"shift+ArrowDown, shift+j",
		() => {
			if (selectedIndex === null) return
			const length = threadsLengthRef.current
			const item = getThreadAtIndexRef.current(selectedIndex)
			if (!item) return
			const isLastItem = selectedIndex === length - 1

			if (isLastItem && !threadIds.includes(item.id)) {
				toggleThreadId(`${item.id}`)
				return
			}

			if (!isLastItem) {
				toggleThreadId(`${item.id}`)
				moveDown()
			}
		},
		{ preventDefault: true },
	)

	useShortcut(
		"shift+ArrowUp, shift+k",
		() => {
			if (selectedIndex === null) return
			const item = getThreadAtIndexRef.current(selectedIndex)
			if (!item) return
			const isFirstItem = selectedIndex === 0

			if (isFirstItem && !threadIds.includes(item.id)) {
				toggleThreadId(`${item.id}`)
				return
			}

			if (!isFirstItem) {
				toggleThreadId(`${item.id}`)
				moveUp()
			}
		},
		{ preventDefault: true },
	)

	useShortcut("ArrowDown, j", moveDown, { preventDefault: true })
	useShortcut("ArrowUp, k", moveUp, { preventDefault: true })

	const zero = useZero()
	useShortcut("u", () => {
		const loaded = new Map<string, NavThread>()
		if (threadIds.length > 0) {
			for (let i = 0; i < threadsLengthRef.current; i++) {
				const thread = getThreadAtIndexRef.current(i)
				if (thread) loaded.set(thread.id, thread)
			}
		}
		const checked = threadIds.map((id) => loaded.get(id) ?? { id })
		const index = currentIndex()
		const cursor =
			index === null ? undefined : getThreadAtIndexRef.current(index)
		for (const batch of readToggle(checked, cursor)) {
			void zero.mutate(mutators.threads.setReadBulk(batch))
		}
	})

	useShortcut(
		"enter",
		() => {
			if (selectedIndex === null) return
			const item = getThreadAtIndexRef.current(selectedIndex)
			if (item) {
				void navigate({ to: "/threads/$id", params: { id: item.id } })
			}
		},
		{
			preventDefault: true,
			enabled: false,
		},
	)

	useShortcut(
		"x",
		() => {
			if (selectedIndex === null) return
			const item = getThreadAtIndexRef.current(selectedIndex)
			if (item) {
				toggleThreadId(`${item.id}`)
			}
		},
		{ preventDefault: true },
	)

	return {
		isKeyboardNav,
		isNavSettling,
		selectedIndex,
		setSelectedIndex,
	}
}

export function useInboxListShortcuts({
	threadTab,
	setThreadTab,
	getThreadAtIndex,
	threadsLength,
	scrollToIndex,
	threadIds,
	toggleThreadId,
	onTabSwitch,
	onNavigate,
	resolveOpenThreadIndex,
	hasOpenThread,
	navShortcutsEnabled,
}: {
	threadTab: InboxState
	setThreadTab: (value: InboxState) => void
	getThreadAtIndex: (index: number) => NavThread | undefined
	threadsLength: number
	scrollToIndex: (index: number) => void
	threadIds: string[]
	toggleThreadId: (id: string) => void
	onTabSwitch?: (direction: "forward" | "backward") => void
	onNavigate?: (thread: NavThread) => void
	resolveOpenThreadIndex?: () => number | null
	hasOpenThread?: boolean
	navShortcutsEnabled?: boolean
}) {
	const inboxVirtualizer = useListVirtualizerShortcuts({
		getThreadAtIndex,
		threadsLength,
		scrollToIndex,
		threadIds,
		toggleThreadId,
		threadTab,
		onNavigate,
		resolveOpenThreadIndex,
		hasOpenThread,
	})

	const { setSelectedIndex } = inboxVirtualizer
	const deselect = useCallback(() => {
		setSelectedIndex(null)
	}, [setSelectedIndex])

	useListTabShortcuts({
		threadTab,
		setThreadTab,
		onTabSwitch,
		onDeselect: deselect,
		navShortcutsEnabled,
	})

	return {
		...inboxVirtualizer,
		getItemProps: getListItemProps({
			threadIds,
			isKeyboardNav: inboxVirtualizer.isKeyboardNav,
			selectedIndex: inboxVirtualizer.selectedIndex,
			setSelectedIndex: inboxVirtualizer.setSelectedIndex,
		}),
	}
}
